import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CHALLENGE_SENT_EVENT, gamesChanged } from "@/lib/games";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import { ArrowRight, Cards, LinkSimple, UsersThree, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { clockLabel, timeControlLabel } from "@/lib/clocks";

const POLL_MS = 15000;

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const BOARD_NAMES = { boardgame: "Original", boardgame2: "Fortress", fortress2: "Fortress 2.0", infinity: "Infinity (old)", infinity2: "Infinity", boardgame3: "Classic", boardgame4: "Firefly (old)", firefly2: "Firefly", boardgame5: "The Wave", bridges: "Bridges (old)", bridges_trial: "Bridges (short)", bridges2: "Bridges", bridges3: "Bridges 2.0", race: "Raider Races", letterheist: "Letterheist",
  vowel_classic: "Vowel Prowl · Classic", vowel_fortress: "Vowel Prowl · Fortress", vowel_firefly: "Vowel Prowl · Firefly", vowel_wave: "Vowel Prowl · The Wave" };
// Boards a friend can be challenged on, offered once the player is picked.
export const CHALLENGE_BOARDS = ["boardgame3", "boardgame2", "fortress2", "infinity2", "firefly2", "boardgame5", "bridges2", "bridges3"];

// "Turn based · No time limit · Fortress" for a rematch offer, so it's clear
// the rematch keeps the last game's format.
// Games and challenges under the unbroken chain rule carry a small pink tag
// (Jack, 2026-10-29: plain text in the subtitle was easy to miss), and
// "Same tiles" games one of their own (Jack, 2026-10-30).
const TAG_CLS = "inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border border-brand/50 bg-brand/10 text-brand text-[10px] font-bold";
function UnbrokenTag({ rules, same }) {
  if (rules !== "unbroken" && !same) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {rules === "unbroken" && (
        <span className={TAG_CLS} data-testid="unbroken-tag">
          <LinkSimple size={11} weight="bold" aria-hidden="true" />Unbroken chain
        </span>
      )}
      {same && (
        <span className={TAG_CLS} data-testid="same-tiles-tag">
          <Cards size={11} weight="bold" aria-hidden="true" />Same tiles
        </span>
      )}
    </span>
  );
}

function rematchFormat(c) {
  const mode = c.simultaneous ? "Same time" : c.mode === "realtime" ? "Real-time" : "Turn based";
  const tc = c.clock ? clockLabel(c.clock) : c.time_control_seconds ? `${c.time_control_seconds}s` : "No time limit";
  const board = BOARD_NAMES[c.layout] || (c.layout || "").replace("_", " ");
  return [mode, tc, board].filter(Boolean).join(" · ");
}

// Open turn-based games against people (any number at once), challenges
// received and sent. Challenging someone lives in Find opponent.
export default function YourGames({ className = "lg:col-span-3" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ games: [], incoming: [], outgoing: [], raceInvites: [] });
  // `loaded` means the list has come back at least once. Until then a failed
  // or slow request must never read as "No games yet" (Jack, 2026-10-14).
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const retryTimer = useRef(null);
  const retryDelay = useRef(2000);
  const [busy, setBusy] = useState(null); // id of the row being acted on
  // Bot game whose X was tapped, waiting for "End game" (Jack, 2026-10-29:
  // close bot games from the list without opening each one).
  const [ending, setEnding] = useState(null);
  // Bot games just ended here. Their rows vanish at once, before the server
  // answers (Jack, 2026-11-02: waiting for the round trip felt laggy), and stay
  // hidden even if a poll that started earlier still lists them.
  const hidden = useRef(new Set());

  const load = useCallback(async () => {
    clearTimeout(retryTimer.current);
    try {
      const { data: d } = await api.get("/me/games", { timeout: 15000 });
      const games = (d.games || []).filter((g) => !hidden.current.has(g.match_id));
      setData({ games, incoming: d.incoming || [], outgoing: d.outgoing || [],
        raceInvites: d.race_invites || [] });
      gamesChanged();
      setLoaded(true);
      setFailed(false);
      retryDelay.current = 2000;
    } catch {
      // Keep what we have, and try again soon (2s, 4s, 8s ... up to 30s)
      // rather than waiting for the next 15s poll.
      setFailed(true);
      const wait = retryDelay.current;
      retryDelay.current = Math.min(wait * 2, 30000);
      retryTimer.current = setTimeout(() => loadRef.current(), wait);
    }
  }, []);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener(CHALLENGE_SENT_EVENT, onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(t);
      clearTimeout(retryTimer.current);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(CHALLENGE_SENT_EVENT, onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  const accept = async (c) => {
    setBusy(c.challenge_id);
    try {
      const { data: r } = await api.post(`/challenges/${c.challenge_id}/accept`);
      navigate(`/match/${r.match_id}?layout=${r.match?.layout || "boardgame3"}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't accept");
      load();
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (c) => {
    setBusy(c.challenge_id);
    try {
      await api.post(`/challenges/${c.challenge_id}/decline`);
    } catch { /* already answered */ }
    setBusy(null);
    load();
  };

  // 3-player Raider Races invites (Jack, 2026-10-29). Accepting opens the
  // race, or the "waiting for everyone" screen until the others are in.
  const decline3 = async (inv) => {
    setBusy(inv.invite_id);
    try { await api.post(`/race3/invites/${inv.invite_id}/decline`); } catch { /* already over */ }
    setBusy(null);
    load();
  };

  // Ends a bot game the same way Resign does (no Elo), so it leaves the list.
  const endBotGame = async (g) => {
    const id = g.match_id;
    hidden.current.add(id);
    setEnding(null);
    setData((d) => ({ ...d, games: d.games.filter((x) => x.match_id !== id) }));
    try {
      // A Det test game lives in its own save (backend/det_saves.py), not a match.
      if (g.det_bot) await api.delete("/det/save");
      // A 4-player game: a bot takes your colour, and it ends once no people
      // are left (backend/four.py leave).
      else if (g.four) await api.post(`/four/games/${g.game_id}/leave`);
      else await api.post(`/match/${g.match_id}/resign`);
      // Polls time out after 15s, so after 20s no earlier poll can bring the
      // row back. Then forget it (a new Det game reuses the "det-bot" id).
      setTimeout(() => hidden.current.delete(id), 20000);
    } catch (e) {
      // It didn't end, so bring the row back.
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't end that game");
      hidden.current.delete(id);
    }
    load();
  };

  const { games, incoming, outgoing, raceInvites } = data;
  const yourTurn = games.filter((g) => g.your_turn && !g.live_race).length;
  const empty = loaded && !games.length && !incoming.length && !outgoing.length && !raceInvites.length;

  return (
    <div className={`${className} bg-surface border border-border rounded-2xl p-4 sm:p-6 md:p-8 scroll-mt-20`} id="your-games" data-testid="your-games-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Your games</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {yourTurn > 0
              ? `It's your move in ${yourTurn} game${yourTurn === 1 ? "" : "s"}.`
              : "Play at your own pace. Have as many games going as you like."}
          </p>
        </div>
        <UsersThree size={28} weight="duotone" className="text-brand flex-none" />
      </div>

      {!loaded && (
        <div className="text-sm text-muted-foreground text-center py-4" data-testid="your-games-loading">
          {failed ? (
            <>
              Can't reach Jangle right now. Trying again…{" "}
              <button type="button" onClick={() => { retryDelay.current = 2000; load(); }} className="font-semibold text-brand underline" data-testid="your-games-retry">
                Retry
              </button>
            </>
          ) : "Loading your games…"}
        </div>
      )}
      {loaded && failed && (
        <p className="text-[11px] text-muted-foreground text-center mb-2" data-testid="your-games-stale">
          Connection lost. Showing your games from a moment ago.
        </p>
      )}

      {empty && (
        <p className="text-sm text-muted-foreground text-center py-4" data-testid="your-games-empty">
          No games yet. Tap Find opponent to challenge someone.
        </p>
      )}

      <ul className="space-y-2.5">
        {incoming.map((c) => (
          <li key={c.challenge_id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-xl border border-brand/40 bg-brand/10" data-testid={`incoming-${c.challenge_id}`}>
            <div className="flex items-center gap-2 min-w-0 flex-1 basis-[8rem]">
              <Avatar userId={c.from_id} username={c.from_username} className="w-9 h-9 text-sm" fallbackClassName="bg-muted text-muted-foreground" />
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate"><PlayerLink id={c.from_id}>{c.from_username}</PlayerLink></div>
                <div className="text-[11px] text-muted-foreground">
                  {c.rematch_of ? `Wants a rematch · ${rematchFormat(c)}` : `Challenged you · ${c.ui === "stream" ? "Stream · " : ""}${BOARD_NAMES[c.layout] || "Fortress"}${c.layout === "race" ? " · 1-minute rounds" : c.time_control_seconds ? ` · ${c.time_control_seconds}s per move` : ""}`} · {ago(c.created_at)}
                </div>
                <UnbrokenTag rules={c.test_rules} same={c.same_tiles} />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-1.5 ml-auto">
              <Button size="sm" variant="ghost" disabled={busy === c.challenge_id} onClick={() => dismiss(c)} className="rounded-full text-muted-foreground" data-testid="decline-challenge-btn">
                Decline
              </Button>
              <Button size="sm" disabled={busy === c.challenge_id} onClick={() => accept(c)} className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="accept-challenge-btn">
                Accept
              </Button>
            </div>
          </li>
        ))}

        {raceInvites.map((inv) => {
          const mine = inv.players.find((p) => p.id === user?.id);
          const others = inv.players.filter((p) => p.id !== user?.id);
          const waitingOn = inv.players.filter((p) => !p.accepted && p.id !== user?.id).map((p) => p.username);
          const fromMe = inv.from_id === user?.id;
          return (
            <li key={inv.invite_id} className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-xl border ${
              mine?.accepted ? "border-border bg-background" : "border-brand/40 bg-brand/10"}`} data-testid={`race3-invite-${inv.invite_id}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1 basis-[8rem]">
                <span className="w-9 h-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center flex-none"><UsersThree size={18} weight="bold" /></span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{fromMe ? `Race vs ${others.map((p) => p.username).join(" and ")}` : `${inv.from_username} invited you to a race`}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Raider Races · 3 players · {mine?.accepted ? `Waiting for ${waitingOn.join(" and ") || "everyone"}` : `with ${others.filter((p) => p.id !== inv.from_id).map((p) => p.username).join(" and ")}`} · {ago(inv.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-1.5 ml-auto">
                {mine?.accepted ? (
                  <Button size="sm" variant="outline" onClick={() => navigate("/race?wait3=1")} className="rounded-full" data-testid="race3-open-btn">
                    Open
                  </Button>
                ) : (<>
                  <Button size="sm" variant="ghost" disabled={busy === inv.invite_id} onClick={() => decline3(inv)} className="rounded-full text-muted-foreground" data-testid="race3-decline-btn">
                    Decline
                  </Button>
                  <Button size="sm" disabled={busy === inv.invite_id} onClick={() => navigate(`/race-invite/${inv.invite_id}`)} className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="race3-accept-btn">
                    Accept
                  </Button>
                </>)}
              </div>
            </li>
          );
        })}

        {games.map((g) => {
          const closable = (g.is_bot || g.four) && !g.finished && !g.bot_race;
          if (ending === g.match_id) return (
            <li key={g.match_id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-xl border border-border bg-background" data-testid={`end-confirm-${g.match_id}`}>
              <div className="text-sm font-semibold min-w-0 flex-1 basis-[8rem]">{g.four ? "Leave this 4-player game? A bot plays your colour." : `End your game vs ${g.opp_username}?`}</div>
              <div className="flex gap-1.5 ml-auto">
                <Button size="sm" variant="ghost" disabled={busy === g.match_id} onClick={() => setEnding(null)} className="rounded-full text-muted-foreground" data-testid="end-bot-game-keep">
                  Keep
                </Button>
                <Button size="sm" disabled={busy === g.match_id} onClick={() => endBotGame(g)} className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="end-bot-game-confirm">
                  {g.four ? "Leave" : "End game"}
                </Button>
              </div>
            </li>
          );
          return (
          <li key={g.match_id} className="relative">
            <button
              type="button"
              onClick={() => navigate(g.bot_race ? "/race?resume=1" : g.det_bot ? "/det?resume=1" : g.four ? `/four?game=${g.game_id}` : `/match/${g.match_id}?layout=${g.layout || "boardgame3"}`)}
              className={`w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-xl border text-left transition-colors ${
                g.finished ? "border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10"
                  : g.your_turn ? "border-brand/50 bg-brand/5 hover:bg-brand/10" : "border-border bg-background hover:border-foreground/30"
              }`}
              data-testid={`game-${g.match_id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1 basis-[8rem]">
                <Avatar userId={g.opp_id} username={g.opp_username} bot={g.is_bot} className="w-9 h-9 text-sm" fallbackClassName="bg-muted text-muted-foreground" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">vs {g.is_bot || g.live_race || g.four ? g.opp_username : <PlayerLink id={g.opp_id}>{g.opp_username}</PlayerLink>}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {g.ui === "stream" ? "Stream · " : ""}{g.four ? "4-player Fortress · " : g.det_bot ? "Det test · " : g.is_bot ? "Bot game · " : ""}{g.four ? `${g.your_turn ? "Your" : `${g.turn_name}'s`} turn · Round ${g.round || 1} of 15` : BOARD_NAMES[g.layout] || "Fortress"} · {g.finished
                      ? `${g.you_won ? "You won" : `${g.opp_username} won`} ${ago(g.completed_at || g.last_move_at)}`
                      : g.bot_race || g.det_bot ? `Round ${g.round || 1} of 15 · Left ${ago(g.last_move_at)}`
                      : g.live_race ? `Started ${ago(g.created_at)}`
                      : `Last move ${ago(g.last_move_at)}`}
                  </div>
                  <UnbrokenTag rules={g.test_rules} same={g.same_tiles} />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-none ml-auto">
                <span
                  className={`text-[11px] uppercase tracking-wider font-bold whitespace-nowrap px-2.5 py-1 rounded-full border ${
                    g.finished ? "bg-amber-500 text-black border-amber-500"
                      : g.your_turn ? "bg-brand text-brand-foreground border-brand" : "text-muted-foreground border-border"
                  }`}
                  data-testid={g.finished ? "game-over-badge" : g.live_race ? "live-race-badge" : undefined}
                >
                  {g.finished ? "Game over" : g.live_race ? "Live race" : g.your_turn ? "Your turn" : "Their turn"}
                </span>
                {closable
                  ? <span className="w-7 flex-none" aria-hidden="true" />
                  : <ArrowRight size={16} weight="bold" className="text-muted-foreground" />}
              </div>
            </button>
            {closable && (
              <button
                type="button"
                onClick={() => setEnding(g.match_id)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-foreground/10"
                aria-label={`End game vs ${g.opp_username}`}
                title="End game"
                data-testid={`end-bot-game-${g.match_id}`}
              >
                <X size={16} weight="bold" />
              </button>
            )}
          </li>
          );
        })}

        {outgoing.map((c) => (
          <li key={c.challenge_id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-xl border border-dashed border-border bg-background" data-testid={`outgoing-${c.challenge_id}`}>
            <div className="flex items-center gap-2 min-w-0 flex-1 basis-[8rem]">
              <Avatar userId={c.to_id} username={c.to_username} className="w-9 h-9 text-sm" fallbackClassName="bg-muted text-muted-foreground" />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate"><PlayerLink id={c.to_id}>{c.to_username}</PlayerLink></div>
                <div className="text-[11px] text-muted-foreground">
                  {c.rematch_of ? `Rematch offer · ${rematchFormat(c)}` : `${c.ui === "stream" ? "Stream · " : ""}${BOARD_NAMES[c.layout] || "Fortress"}${c.layout === "race" ? " · 1-minute rounds" : c.time_control_seconds ? ` · ${c.time_control_seconds}s per move` : ""} · waiting for them to accept`} · sent {ago(c.created_at)}
                </div>
                <UnbrokenTag rules={c.test_rules} same={c.same_tiles} />
              </div>
            </div>
            <Button size="sm" variant="ghost" disabled={busy === c.challenge_id} onClick={() => dismiss(c)} className="rounded-full text-muted-foreground flex-none ml-auto" data-testid="cancel-challenge-btn">
              <X size={14} className="mr-1" /> Cancel
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
